import Modal from '@/common/components/Modal'
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react'
import Button from '@/common/components/Button';
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextField from '@/common/components/TextField';
import { EstablishmentCompanyData } from '@/common/types/establishment/company.type';
import MaskedTextField from '@/common/components/MaskedTextField';
import { phoneMask } from '@/common/constants/masks.constant';
import useUpdateGeneralInformationContact from '../../hooks/useUpdateGeneralInformationContact';
import { updateGeneralInformationContactSchema, UpdateGeneralInformationContactValidationSchema } from '../../schemas/updateGeneralInformationContact.schema';
import { FormTransformers } from '@/common/utils/formTransformers';

interface ContactInfoUpdateModalProps {
  establishmentMatrixBranchId: string;
  establishmentCompanyId: string;
  generalInformation: EstablishmentCompanyData['general_information'];
  disabled?: boolean;
}

const ContactInfoUpdateModal = ({
  establishmentMatrixBranchId,
  establishmentCompanyId,
   generalInformation,
   disabled,
  }: ContactInfoUpdateModalProps) => {
  const queryClient = useQueryClient();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<UpdateGeneralInformationContactValidationSchema>({
    resolver: zodResolver(updateGeneralInformationContactSchema),
  });

  const { mutate: updateGeneralInformationContact } = useUpdateGeneralInformationContact({
    establishmentCompanyId: establishmentMatrixBranchId,
    generalInformationId: generalInformation?.id ?? '',
    onSuccess: () => {
      setModalIsOpen(false);
      setMessages(['Dados atualizado com sucesso!'])
      queryClient.invalidateQueries({queryKey: ["establishmentCompany", establishmentCompanyId]})
    },
    onError: FormTransformers.errorsTransformer<UpdateGeneralInformationContactValidationSchema>({ setError, setMessages })
  });

  const onSubmit: SubmitHandler<UpdateGeneralInformationContactValidationSchema> = (data) => updateGeneralInformationContact(data);

  useEffect(() => {
    reset()
  }, [modalIsOpen])

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        setIsOpen={setModalIsOpen}
        triggerDisabled={disabled}
        Trigger={
          <Button
            operationKlassToCheckPermission='Admin::Establishment::Companies::Update'
            data-testid='update_general_information_contact_button'
            disabled={disabled}
          >
            Editar
          </Button>
        }
        useCancellableFooter
        showClose={false}
        title="Detalhes dados gerais"
        titleClass='text-2xl'
        cancelButtonClassName='bg-white-100'
        subtitleClassName="mt-2 text-sm"
        confirmButtonClassName="bg-blue-700"
        bodyClassName='text-white-800'
        body={(
           <form>
              <div className="flex flex-col gap-4">
                <TextField
                  label="Endereço eletrônico"
                  maxLength={64}
                  defaultValue={generalInformation?.email}
                  disabled
                />

                <MaskedTextField
                  label="Telefone"
                  mask={phoneMask}
                  dataTestid="update_phone_number_input"
                  defaultValue={generalInformation?.phone?.number}
                  errorMessage={errors?.general_information?.phone?.number?.message || undefined}
                  {...register('general_information.phone.number')}
                />
              </div>
            </form>
        )}
        onConfirm={handleSubmit(onSubmit)}
      />
      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          onClose={() => {
            queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompanyId] });
          }}
        />
      )}
    </>
  )
}

export default ContactInfoUpdateModal;
